import React, { useState, useMemo } from 'react';
import { Sparkles, AlertCircle, CheckCircle2, Grid3X3, List } from 'lucide-react';
import { TODAY } from '../utils/dates';
import CyclingTitle from './primitives/CyclingTitle';
import css from './VinylPage.module.css';

// ── Status + Source badges ────────────────────────────────────────────────────
const STATUS_COLOR = { collection:'var(--accent-s)', shipped:'var(--accent-g)', ordered:'var(--t4)', wishlist:'var(--accent-g)' };

function StatusBadge({ s }) {
  const label = s === 'collection' ? 'delivered' : s === 'shipped' ? 'shipped' : s === 'wishlist' ? 'wishlist' : 'ordered';
  return <span className={css.badge} style={{ borderColor: STATUS_COLOR[s] || 'var(--b2)', color: STATUS_COLOR[s] || 'var(--t3)' }}>{label}</span>;
}

// ── Cover art fallback ────────────────────────────────────────────────────────
function CoverArt({ src, artist, size = 'md' }) {
  const sz = size === 'lg' ? 72 : size === 'sm' ? 40 : 120;
  if (src) return <img src={src} alt="" className={css[`cover${size === 'lg' ? 'Lg' : size === 'sm' ? 'Sm' : 'Grid'}`]} />;
  return (
    <div className={css[`coverFallback${size === 'lg' ? 'Lg' : size === 'sm' ? 'Sm' : ''}`]}>
      <span className={css.coverInitial}>{(artist || '?')[0].toUpperCase()}</span>
    </div>
  );
}

export default function VinylPage({ vinyl=[], markDelivered, markWishlist, markOrdered, showAdd, setShowAdd, setSt, notify, savedDiscogsToken, savedDiscogsUser, nowPlayingId, setNowPlaying, clearNowPlaying }) {

  // ── All state ─────────────────────────────────────────────────────────────
  const [form, setForm] = useState({ artist:'', album:'', vendor:'', ordered:'', eta:'', status:'ordered', tracking:'', genre:'', format:'LP' });
  const [activeTab, setActiveTab] = useState('collection');
  const [formMode, setFormMode]   = useState('ordered'); // 'ordered' | 'wishlist'
  const [searchQ, setSearchQ]     = useState('');
  const [filterYear, setFilterYear]   = useState('');
  const [filterLabel, setFilterLabel] = useState('');
  const [filterGenre, setFilterGenre] = useState('');
  const [filterFormat, setFilterFormat] = useState(''); // kept for add-form only, not used in filters
  const closeTimeout = React.useRef(null);
  const [sortBy, setSortBy]   = useState('artist');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [discogs, setDiscogs] = useState({
    username: savedDiscogsUser || (() => { try { return localStorage.getItem('mylo_discogs_user') || ''; } catch { return ''; } })(),
    token: savedDiscogsToken || (() => { try { return localStorage.getItem('mylo_discogs_token') || ''; } catch { return ''; } })(),
    importing:false, imported:!!savedDiscogsToken, importedCount:0, totalPages:0, currentPage:0, error:'',
  });
  const [showDiscogs, setShowDiscogs] = useState(false);
  const [enriching, setEnriching]     = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [emailText, setEmailText]     = useState('');
  const [emailScan, setEmailScan]     = useState({ scanning:false, found:null });

  // Cleanup auto-close timeout on unmount
  // ── Derived ──────────────────────────────────────────────────────────────
  const orders            = vinyl.filter(v => v.status === 'ordered' || (!v.status && !v.delivered));
  const collection        = vinyl.filter(v => v.status === 'collection' || (!v.status && v.delivered));
  const wishlist          = vinyl.filter(v => v.status === 'wishlist');
  const collectionAndOrders = vinyl.filter(v => v.status === 'collection' || v.status === 'ordered' || (!v.status));

  // Cleanup auto-close timeout on unmount
  React.useEffect(() => () => { if (closeTimeout.current) clearTimeout(closeTimeout.current); }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const isDuplicate = (artist, album, discogsId) =>
    vinyl.some(v => {
      if (discogsId && v.discogsId && String(v.discogsId) === String(discogsId)) return true;
      return v.artist.toLowerCase().trim() === artist.toLowerCase().trim() &&
             v.album.toLowerCase().trim()  === album.toLowerCase().trim();
    });

  const normalizeArtist = (raw = '') =>
    raw.replace(/\s*\(\d+\)$/, '').replace(/^(.+),\s*(The|A|An)$/i, '$2 $1').trim();

  // ── Discogs API ───────────────────────────────────────────────────────────
  const DISCOGS_BASE = 'https://api.discogs.com';

  async function discogsGet(path) {
    const sep = path.includes('?') ? '&' : '?';
    const url = `${DISCOGS_BASE}${path}${sep}token=${discogs.token}`;
    const res = await fetch(url, {
      headers: { 'User-Agent':'Mylo/1.0 +https://mylo-app-omega.vercel.app', 'Authorization':`Discogs token=${discogs.token}` }
    });
    if (res.status === 401) throw new Error('Invalid Discogs token. Check discogs.com/settings/developers');
    if (res.status === 404) throw new Error('Discogs user not found. Check your username.');
    if (res.status === 429) throw new Error('Discogs rate limit hit — wait 60 seconds and try again.');
    if (!res.ok) throw new Error(`Discogs API error: HTTP ${res.status}`);
    return res.json();
  }

  async function importDiscogs() {
    if (!discogs.username || !discogs.token) { setDiscogs(p => ({ ...p, error:'Username and token required.' })); return; }
    setDiscogs(p => ({ ...p, importing:true, error:'', importedCount:0, currentPage:0 }));
    try {
      const fd = await discogsGet(`/users/${discogs.username}/collection/folders/0/releases?per_page=100&page=1`);
      const pages = fd.pagination?.pages || 1;
      setDiscogs(p => ({ ...p, totalPages:pages, currentPage:1 }));
      let releases = [...(fd.releases || [])];
      for (let pg = 2; pg <= pages; pg++) {
        setDiscogs(p => ({ ...p, currentPage:pg }));
        try { const pd = await discogsGet(`/users/${discogs.username}/collection/folders/0/releases?per_page=100&page=${pg}`); releases = [...releases, ...(pd.releases || [])]; } catch {}
        await new Promise(r => setTimeout(r, 500));
      }
      let added = 0, skipped = 0;
      const toAdd = [];
      for (const r of releases) {
        const bi = r.basic_information || {};
        const artist = normalizeArtist(bi.artists?.[0]?.name || 'Unknown');
        const album  = bi.title || 'Unknown';
        const discogsId = r.id;
        if (isDuplicate(artist, album, discogsId)) { skipped++; continue; }
        const linkedOrder = vinyl.find(v => v.status !== 'collection' && !v.delivered && v.artist.toLowerCase().trim() === artist.toLowerCase().trim() && v.album.toLowerCase().trim() === album.toLowerCase().trim());
        toAdd.push({ id:'dc'+discogsId, artist, album, year:bi.year||null, label:bi.labels?.[0]?.name||'', catNum:bi.labels?.[0]?.catno||'', format:bi.formats?.[0]?.name||'Vinyl', genre:bi.genres?.[0]||bi.styles?.[0]||'', thumb:bi.cover_image||bi.thumb||'', vendor:'Discogs Collection', ordered:TODAY, eta:'', status:'collection', delivered:true, tracking:'', discogsId, dateAdded:TODAY, linkedOrderId:linkedOrder?.id||null, source:'discogs' });
        added++;
      }
      setSt(p => {
        const updated = p.vinyl.map(v => { const m = toAdd.find(r => r.linkedOrderId === v.id); return m ? { ...v, linkedDiscogsId:m.discogsId } : v; });
        return { ...p, vinyl:[...updated, ...toAdd] };
      });
      setDiscogs(p => ({ ...p, importing:false, imported:true, importedCount:added }));
      notify('Imported ' + added + ' records from Discogs');
      if (added > 0) {
        setActiveTab('collection');
        // Auto-close panel after showing success
        closeTimeout.current = setTimeout(() => { setShowDiscogs(false); closeTimeout.current = null; }, 2500);
      }
    } catch (err) {
      setDiscogs(p => ({ ...p, importing:false, error:err.message }));
    }
  }

  async function enrichRecord(vinylId, token) {
    const t = token || discogs.token;
    const rec = vinyl.find(v => v.id === vinylId);
    if (!rec || !t || !rec.discogsId) return;
    setEnriching(vinylId);
    try {
      const res = await fetch(`${DISCOGS_BASE}/releases/${rec.discogsId}?token=${t}`, { headers:{ 'User-Agent':'Mylo/1.0', 'Authorization':`Discogs token=${t}` } });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const d = await res.json();
      const primaryImg = d.images?.find(img => img.type === 'primary') || d.images?.[0];
      const patch = { year:d.year||rec.year, label:d.labels?.[0]?.name||rec.label, catNum:d.labels?.[0]?.catno||rec.catNum, genre:d.genres?.[0]||d.styles?.[0]||rec.genre, format:d.formats?.[0]?.name||rec.format, thumb:primaryImg?.uri||primaryImg?.uri150||d.thumb||rec.thumb, coverArt:primaryImg?.uri||rec.coverArt||'', country:d.country||'', tracklist:(d.tracklist||[]).map(t=>t.title).slice(0,20), notes:d.notes||'', discogsUrl:d.uri||'', enriched:true };
      setSt(p => ({ ...p, vinyl:p.vinyl.map(v => v.id === vinylId ? {...v,...patch} : v) }));
      if (selectedRecord?.id === vinylId) setSelectedRecord(v => ({...v,...patch}));
      notify('Metadata enriched');
    } catch(e) { notify('Enrichment failed'); }
    setEnriching(null);
  }

  async function searchAndEnrich(vinylId) {
    const t = discogs.token;
    const rec = vinyl.find(v => v.id === vinylId);
    if (!rec || !t) { notify('Discogs token required'); return; }
    setEnriching(vinylId);
    try {
      const q = encodeURIComponent(rec.artist + ' ' + rec.album);
      const res = await fetch(`${DISCOGS_BASE}/database/search?q=${q}&type=release&per_page=5&token=${t}`, { headers:{ 'User-Agent':'Mylo/1.0', 'Authorization':`Discogs token=${t}` } });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      const hit  = data.results?.[0];
      if (!hit) { notify('No Discogs match found'); setEnriching(null); return; }
      setSt(p => ({ ...p, vinyl:p.vinyl.map(v => v.id === vinylId ? { ...v, discogsId:hit.id, thumb:hit.thumb||v.thumb, year:hit.year||v.year, label:hit.label?.[0]||v.label, genre:hit.genre?.[0]||hit.style?.[0]||v.genre, format:hit.format?.[0]||v.format } : v) }));
      setTimeout(async () => {
        const updated = { ...rec, discogsId:hit.id };
        setEnriching(vinylId);
        try {
          const r2 = await fetch(`${DISCOGS_BASE}/releases/${hit.id}?token=${t}`, { headers:{ 'User-Agent':'Mylo/1.0', 'Authorization':`Discogs token=${t}` } });
          if (!r2.ok) throw new Error('HTTP ' + r2.status);
          const d2 = await r2.json();
          const patch = { year:d2.year||updated.year, label:d2.labels?.[0]?.name||updated.label, genre:d2.genres?.[0]||d2.styles?.[0]||updated.genre, format:d2.formats?.[0]?.name||updated.format, thumb:d2.images?.[0]?.uri150||hit.thumb||updated.thumb, coverArt:d2.images?.[0]?.uri||'', country:d2.country||'', tracklist:(d2.tracklist||[]).map(t=>t.title).slice(0,20), notes:d2.notes||'', discogsUrl:d2.uri||'', enriched:true };
          setSt(p => ({ ...p, vinyl:p.vinyl.map(v => v.id === vinylId ? {...v,...patch} : v) }));
          if (selectedRecord?.id === vinylId) setSelectedRecord(v => ({...v,...patch}));
          notify('Metadata enriched');
        } catch(e) { /* silent */ }
        setEnriching(null);
      }, 400);
    } catch(e) { notify('Search failed'); setEnriching(null); }
  }

  async function importDiscogsWishlist() {
    if (!discogs.username || !discogs.token) { setDiscogs(p => ({ ...p, error:'Username and token required.' })); return; }
    setDiscogs(p => ({ ...p, importing:true, error:'', importedCount:0, currentPage:0 }));
    try {
      const fd = await discogsGet(`/users/${discogs.username}/wants?per_page=100&page=1`);
      const pages = fd.pagination?.pages || 1;
      setDiscogs(p => ({ ...p, totalPages:pages, currentPage:1 }));
      let wants = [...(fd.wants || [])];
      for (let pg = 2; pg <= pages; pg++) {
        setDiscogs(p => ({ ...p, currentPage:pg }));
        try { const pd = await discogsGet(`/users/${discogs.username}/wants?per_page=100&page=${pg}`); wants = [...wants, ...(pd.wants || [])]; } catch {}
        await new Promise(r => setTimeout(r, 500));
      }
      let added = 0;
      const toAdd = [];
      for (const r of wants) {
        const bi = r.basic_information || {};
        const artist = normalizeArtist(bi.artists?.[0]?.name || 'Unknown');
        const album  = bi.title || 'Unknown';
        const discogsId = r.id;
        if (isDuplicate(artist, album, discogsId)) continue;
        toAdd.push({ id:'dw'+discogsId, artist, album, year:bi.year||null, label:bi.labels?.[0]?.name||'', catNum:bi.labels?.[0]?.catno||'', format:bi.formats?.[0]?.name||'Vinyl', genre:bi.genres?.[0]||bi.styles?.[0]||'', thumb:bi.cover_image||bi.thumb||'', vendor:'', ordered:'', eta:'', status:'wishlist', delivered:false, tracking:'', discogsId, dateAdded:TODAY, source:'discogs' });
        added++;
      }
      setSt(p => ({ ...p, vinyl:[...p.vinyl, ...toAdd] }));
      setDiscogs(p => ({ ...p, importing:false, imported:true, importedCount:added }));
      notify('Imported ' + added + ' wishlist records from Discogs');
      if (added > 0) {
        setActiveTab('wishlist');
        closeTimeout.current = setTimeout(() => { setShowDiscogs(false); closeTimeout.current = null; }, 2500);
      }
    } catch (err) {
      setDiscogs(p => ({ ...p, importing:false, error:err.message }));
    }
  }
  const handleAdd = () => {
    if (!form.artist.trim() || !form.album.trim()) return;
    if (isDuplicate(form.artist, form.album, null)) { notify('Already in your vinyl'); return; }
    const newStatus = formMode === 'wishlist' ? 'wishlist' : form.status === 'delivered' ? 'collection' : form.status;
    const newR = { id:'v'+Date.now(), artist:form.artist.trim(), album:form.album.trim(),
      vendor:formMode==='wishlist'?'':form.vendor, ordered:formMode==='wishlist'?'':form.ordered||TODAY,
      eta:formMode==='wishlist'?'':form.eta, status:newStatus, tracking:formMode==='wishlist'?'':form.tracking,
      genre:form.genre, format:form.format, delivered:newStatus==='collection', dateAdded:TODAY, source:'manual', thumb:'', discogsId:null };
    setSt(p => ({ ...p, vinyl:[newR,...p.vinyl] }));
    setForm({ artist:'', album:'', vendor:'', ordered:'', eta:'', status:'ordered', tracking:'', genre:'', format:'LP' });
    setFormMode('ordered');
    setShowAdd(false);
    notify(formMode === 'wishlist' ? 'Added to wishlist' : 'Order saved');
    if (discogs.token && newStatus !== 'wishlist') setTimeout(() => searchAndEnrich(newR.id), 600);
  };

  const updateStatus = (id, s) => {
    const isCollection = s === 'delivered' || s === 'collection';
    setSt(p => ({ ...p, vinyl:p.vinyl.map(v => v.id===id ? {...v, status: isCollection ? 'collection' : s, delivered:isCollection, ...(isCollection?{deliveryDate:TODAY,dateAdded:TODAY}:{})} : v) }));
    notify(isCollection ? 'Moved to collection' : 'Status updated');
  };

  const deleteVinyl = id => {
    setSt(p => ({ ...p, vinyl:p.vinyl.filter(v => v.id!==id) }));
    if (selectedRecord?.id === id) setSelectedRecord(null);
    notify('Removed');
  };

  function matchOrderToCollection(orderId) {
    const order = vinyl.find(v => v.id===orderId);
    if (!order) return;
    const match = collection.find(v => v.artist.toLowerCase().trim()===order.artist.toLowerCase().trim() && v.album.toLowerCase().trim()===order.album.toLowerCase().trim());
    if (match) {
      setSt(p => ({ ...p, vinyl:p.vinyl.map(v => v.id===orderId?{...v,linkedDiscogsId:match.discogsId}:v) }));
      notify('Linked to collection');
    } else notify('No collection match found');
  }

  // ── Email scanning ────────────────────────────────────────────────────────
  async function scanEmail() {
    if (!emailText.trim()) return;
    setEmailScan({ scanning:true, found:null });
    try {
      const res = await fetch('/api/chat', { method:'POST', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ model:'claude-sonnet-4-20250514', max_tokens:500,
          system:`Extract vinyl order info from email. Return ONLY JSON, no markdown.\nIf not a vinyl order: {"isVinylOrder":false}\nIf vinyl order: {"isVinylOrder":true,"artist":"...","album":"...","orderDate":"YYYY-MM-DD or null","estimatedDelivery":"YYYY-MM-DD or null","retailer":"...","tracking":"... or null","status":"ordered"}`,
          messages:[{ role:'user', content:'Parse this email:\n\n' + emailText.slice(0,2000) }],
        })
      });
      const data = await res.json();
      const raw  = (data.content?.[0]?.text||'{}').replace(/```json|```/g,'').trim();
      const p    = JSON.parse(raw);
      if (!p.isVinylOrder) { notify('No vinyl order found'); setEmailScan({ scanning:false, found:null }); return; }
      const order = { id:'v'+Date.now(), artist:p.artist||'Unknown', album:p.album||'Unknown', vendor:p.retailer||'Unknown', ordered:p.orderDate||TODAY, eta:p.estimatedDelivery||'', tracking:p.tracking||'', status:'ordered', delivered:false, dateAdded:TODAY, source:'email', thumb:'', discogsId:null };
      setSt(p2 => ({ ...p2, vinyl:[order,...p2.vinyl] }));
      notify('Order detected: ' + order.artist + ' — ' + order.album);
      setEmailScan({ scanning:false, found:order });
      setActiveTab('collection');
      if (discogs.token) setTimeout(() => searchAndEnrich(order.id), 800);
    } catch(e) { setEmailScan({ scanning:false, found:null }); notify('Scan failed'); }
  }

  // ── Filters + sort ────────────────────────────────────────────────────────
  const years   = useMemo(() => [...new Set(collectionAndOrders.map(v=>v.year).filter(Boolean))].sort((a,b)=>b-a), [collectionAndOrders]);
  const labels  = useMemo(() => [...new Set(collectionAndOrders.map(v=>v.label).filter(Boolean))].sort(), [collectionAndOrders]);
  const genres  = useMemo(() => [...new Set(collectionAndOrders.map(v=>v.genre).filter(Boolean))].sort(), [collectionAndOrders]);

  const filteredCollection = useMemo(() => collectionAndOrders
    .filter(v => {
      const q = searchQ.toLowerCase();
      if (q && !v.artist.toLowerCase().includes(q) && !v.album.toLowerCase().includes(q) && !(v.label||'').toLowerCase().includes(q)) return false;
      if (filterYear   && String(v.year||'') !== filterYear)  return false;
      if (filterLabel  && (v.label||'').toLowerCase() !== filterLabel.toLowerCase())  return false;
      if (filterGenre  && (v.genre||'').toLowerCase() !== filterGenre.toLowerCase())  return false;
      return true;
    })
    .sort((a,b) => {
      // Incoming (ordered) float to top within sort
      if (a.status === 'ordered' && b.status !== 'ordered') return -1;
      if (b.status === 'ordered' && a.status !== 'ordered') return 1;
      if (sortBy==='artist') return a.artist.localeCompare(b.artist);
      if (sortBy==='year')   return (b.year||0)-(a.year||0);
      if (sortBy==='added')  return (b.dateAdded||'').localeCompare(a.dateAdded||'');
      if (sortBy==='label')  return (a.label||'').localeCompare(b.label||'');
      return 0;
    }), [collectionAndOrders, searchQ, filterYear, filterLabel, filterGenre, sortBy]);

  const activeFilterCount = [filterYear, filterLabel, filterGenre].filter(Boolean).length;

  // ── Pagination ────────────────────────────────────────────────────────────
  const [visibleCount, setVisibleCount] = useState(24);
  React.useEffect(() => { setVisibleCount(24); }, [activeTab, searchQ, filterYear, filterLabel, filterGenre, sortBy, viewMode]);

  // ── CoverArt component — handles missing/broken images ────────────────────
  function CoverArt({ record, size }) {
    const [errored, setErrored] = useState(false);
    const src = record.thumb || record.coverArt || record.imageUrl || '';
    const initial = (record.artist || '?')[0].toUpperCase();
    const isGrid = size === 'grid';
    if (src && !errored) {
      return (
        <img
          src={src} alt={`${record.artist} — ${record.album}`}
          className={isGrid ? css.coverGrid : css.coverList}
          onError={() => setErrored(true)}
        />
      );
    }
    return <div className={isGrid ? css.coverFallbackGrid : css.coverFallbackList}><span className={css.coverInitial}>{initial}</span></div>;
  }

  // ── IncomingCoverGrid — dimmed cover with auburn border for ordered records ──
  function IncomingCoverGrid({ record }) {
    const [errored, setErrored] = useState(false);
    const src = record.thumb || record.coverArt || record.imageUrl || '';
    const initial = (record.artist || '?')[0].toUpperCase();
    if (src && !errored) {
      return <img src={src} alt="" className={css.incomingCoverGrid} onError={() => setErrored(true)} />;
    }
    return (
      <div className={css.coverFallbackGrid} style={{ border:'0.5px solid #B5694A', opacity:0.6 }}>
        <span className={css.coverInitial} style={{ fontSize:24 }}>{initial}</span>
      </div>
    );
  }

  return (
    <div className={css.page}>

      {/* ── Header ── */}
      <div className={css.hdr}>
        <CyclingTitle
          pageLabel="vinyl"
          filters={['collection', 'wishlist', 'stats']}
          value={activeTab}
          onChange={setActiveTab}
          alwaysShowSuffix={true}
        />
        <div className={css.hdrActions}>
          <button className={css.btnPrimary} onClick={() => setShowAdd(p => !p)}>+ order</button>
          <button className={css.btnSecondary} onClick={() => setShowDiscogs(p => !p)} title="Discogs settings" style={{ padding:'8px 10px', minWidth:0 }}>⚙</button>
        </div>
      </div>

      {/* ── Now playing pinned card ── */}
      {(() => {
        if (!nowPlayingId) return null;
        const rec = vinyl.find(v => v.id === nowPlayingId);
        if (!rec) { clearNowPlaying?.(); return null; }
        const src = rec.thumb || rec.coverArt || rec.imageUrl || '';
        return (
          <div className={css.nowPlayingCard} onClick={() => setSelectedRecord(rec)}>
            <div className={css.nowPlayingCover}>
              {src
                ? <img src={src} alt="" className={css.nowPlayingImg} onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                : null}
              <div className={css.nowPlayingFallback} style={{ display: src ? 'none' : 'flex' }}>
                <span className={css.coverInitial} style={{ fontSize:24 }}>{(rec.artist||'?')[0].toUpperCase()}</span>
              </div>
            </div>
            <div className={css.nowPlayingInfo}>
              <div className={css.nowPlayingLabel}>now spinning</div>
              <div className={css.nowPlayingTitle}>{rec.artist} — {rec.album}</div>
              {(rec.year || rec.label) && (
                <div className={css.nowPlayingMeta}>{[rec.year, rec.label].filter(Boolean).join(' · ')}</div>
              )}
              {rec.playingSince && (() => {
                const since = new Date(rec.playingSince);
                const h = since.getHours(); const m = since.getMinutes();
                return <div className={css.nowPlayingMeta}>since {h%12||12}:{String(m).padStart(2,'0')}{h>=12?'pm':'am'}</div>;
              })()}
            </div>
            <button className={css.nowPlayingClear} onClick={e => { e.stopPropagation(); clearNowPlaying?.(); }}>× clear</button>
          </div>
        );
      })()}

      {/* ── Stats pulse ── */}
      <div className={css.pulse} style={{ gridTemplateColumns: orders.length > 0 ? 'repeat(3,1fr)' : 'repeat(2,1fr)' }}>
        <div className={css.pulseCell} onClick={() => setActiveTab('collection')} style={{ background: activeTab==='collection' ? 'color-mix(in srgb, var(--accent) 6%, var(--s1))' : 'var(--s1)', border: activeTab==='collection' ? '0.5px solid color-mix(in srgb, var(--accent) 30%, var(--b1))' : '0.5px solid var(--b1)', borderRadius:'var(--r3)', padding:'14px 16px', cursor:'pointer', transition:'background 0.12s, border-color 0.12s', WebkitTapHighlightColor:'transparent' }}>
          <div className={css.pulseN} style={{ color: activeTab==='collection' ? 'var(--accent)' : 'var(--ink)', fontWeight:300, fontSize:'var(--fs-xl)', letterSpacing:'-0.3px', lineHeight:1.1, marginBottom:4 }}>{collectionAndOrders.length}</div>
          <div className={css.pulseL} style={{ fontFamily:'var(--sans)', fontSize:'var(--fs-xs)', color:'var(--t3)', fontWeight:400 }}>collection</div>
        </div>
        {orders.length > 0 && (
          <div className={css.pulseCell} onClick={() => setActiveTab('collection')} style={{ background:'var(--s1)', border:'0.5px solid var(--b1)', borderRadius:'var(--r3)', padding:'14px 16px', cursor:'pointer', WebkitTapHighlightColor:'transparent' }}>
            <div className={css.pulseN} style={{ color:'var(--accent)', fontWeight:300, fontSize:'var(--fs-xl)', letterSpacing:'-0.3px', lineHeight:1.1, marginBottom:4 }}>{orders.length}</div>
            <div className={css.pulseL} style={{ fontFamily:'var(--sans)', fontSize:'var(--fs-xs)', color:'var(--t3)', fontWeight:400 }}>incoming</div>
          </div>
        )}
        <div className={css.pulseCell} onClick={() => setActiveTab('wishlist')} style={{ background: activeTab==='wishlist' ? 'color-mix(in srgb, var(--accent) 6%, var(--s1))' : 'var(--s1)', border: activeTab==='wishlist' ? '0.5px solid color-mix(in srgb, var(--accent) 30%, var(--b1))' : '0.5px solid var(--b1)', borderRadius:'var(--r3)', padding:'14px 16px', cursor:'pointer', transition:'background 0.12s, border-color 0.12s', WebkitTapHighlightColor:'transparent' }}>
          <div className={css.pulseN} style={{ color: activeTab==='wishlist' ? 'var(--accent)' : 'var(--t2)', fontWeight:300, fontSize:'var(--fs-xl)', letterSpacing:'-0.3px', lineHeight:1.1, marginBottom:4 }}>{wishlist.length}</div>
          <div className={css.pulseL} style={{ fontFamily:'var(--sans)', fontSize:'var(--fs-xs)', color:'var(--t3)', fontWeight:400 }}>wishlist</div>
        </div>
      </div>



      {/* ── Discogs panel ── */}
      {showDiscogs && (
        <div className={css.card}>
          <div className={css.cardHdr}>
            <span className={css.cardTitle}>discogs</span>
            {discogs.imported && <span className={css.badgeConnected}>connected</span>}
          </div>
          <div className={css.cardSub}>
            Token from <span className={css.accentMono}>discogs.com/settings/developers</span>
          </div>
          <div className={css.field}>
            <label className={css.label}>username</label>
            <input className={css.input} value={discogs.username}
              onChange={e => { setDiscogs(p=>({...p,username:e.target.value})); setSt(p=>({...p,profile:{...p.profile,discogsUser:e.target.value}})); }}
              placeholder="your_username" autoComplete="username" />
          </div>
          <div className={css.field}>
            <label className={css.label}>personal access token</label>
            <input className={css.input} type="password" value={discogs.token}
              onChange={e => { setDiscogs(p=>({...p,token:e.target.value})); try{localStorage.setItem('mylo_discogs_token',e.target.value);}catch{}; setSt(p=>({...p,discogsToken:e.target.value})); }}
              placeholder="••••••••••••••••" autoComplete="off" />
          </div>
          {discogs.error && <div className={css.errorMsg}><AlertCircle size={13} strokeWidth={1.5} style={{display:'inline',verticalAlign:'middle',marginRight:4}}/>{discogs.error}</div>}
          {discogs.importing && <div className={css.importingMsg}>importing page {discogs.currentPage} of {discogs.totalPages}…</div>}
          <div className={css.btnRow}>
            <button className={css.btnPrimary} onClick={importDiscogs} disabled={discogs.importing}>
              {discogs.importing ? 'importing…' : 'import collection'}
            </button>
            <button className={css.btnSecondary} onClick={importDiscogsWishlist} disabled={discogs.importing}>
              import wishlist
            </button>
            <button className={css.btnSecondary} onClick={() => setShowDiscogs(false)}>close</button>
            {discogs.imported && <span className={css.importedNote}><CheckCircle2 size={12} strokeWidth={1.5} style={{display:'inline',verticalAlign:'middle',marginRight:3}}/>{discogs.importedCount} imported</span>}
          </div>

          {/* Email scan sub-section */}
          <div className={css.emailSection}>
            <div className={css.cardTitle} style={{ fontSize:'var(--fs-sm)' }}>email order detection</div>
            <div className={css.cardSub}>Paste an order confirmation email to extract order details automatically.</div>
            <div className={css.field}>
              <label className={css.label}>order confirmation email</label>
              <textarea className={css.textarea} value={emailText} onChange={e => setEmailText(e.target.value)} placeholder="Paste a vinyl order email here…" />
            </div>
            <div className={css.btnRow}>
              <button className={css.btnPrimary} onClick={scanEmail} disabled={emailScan.scanning || !emailText.trim()}>
                {emailScan.scanning ? 'scanning…' : 'detect order'}
              </button>
              {emailScan.found && <span className={css.importedNote}><CheckCircle2 size={12} strokeWidth={1.5} style={{display:'inline',verticalAlign:'middle',marginRight:3}}/>{emailScan.found.artist} — {emailScan.found.album}</span>}
            </div>
          </div>
        </div>
      )}

      {/* ── Add order form ── */}
      {showAdd && (
        <div className={css.card}>
          <div className={css.cardHdr}>
            <span className={css.cardTitle}>{formMode === 'wishlist' ? 'add to wishlist' : 'new order'}</span>
            <div className={css.formModeToggle}>
              <button className={[css.formModeBtn, formMode==='ordered'?css.formModeBtnActive:''].filter(Boolean).join(' ')} onClick={()=>setFormMode('ordered')}>order</button>
              <button className={[css.formModeBtn, formMode==='wishlist'?css.formModeBtnActive:''].filter(Boolean).join(' ')} onClick={()=>setFormMode('wishlist')}>wishlist</button>
            </div>
          </div>
          <div className={css.grid2}>
            <div className={css.field}><label className={css.label}>artist <span className={css.req}>·</span></label><input className={css.input} value={form.artist} onChange={e=>setForm(p=>({...p,artist:e.target.value}))} placeholder="e.g. Miles Davis" autoFocus /></div>
            <div className={css.field}><label className={css.label}>album <span className={css.req}>·</span></label><input className={css.input} value={form.album} onChange={e=>setForm(p=>({...p,album:e.target.value}))} placeholder="e.g. Kind of Blue" /></div>
          </div>
          <div className={css.grid2}>
            {formMode !== 'wishlist' && <div className={css.field}><label className={css.label}>vendor</label><input className={css.input} value={form.vendor} onChange={e=>setForm(p=>({...p,vendor:e.target.value}))} placeholder="e.g. Rough Trade" /></div>}
            <div className={css.field}><label className={css.label}>format</label>
              <select className={css.select} value={form.format} onChange={e=>setForm(p=>({...p,format:e.target.value}))}>
                <option>LP</option><option>EP</option><option>Single</option><option>12"</option><option>7"</option><option>Box Set</option>
              </select>
            </div>
          </div>
          <div className={css.grid2}>
            {formMode !== 'wishlist' && <div className={css.field}><label className={css.label}>status</label>
              <select className={css.select} value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>
                <option value="ordered">ordered</option><option value="shipped">shipped</option><option value="delivered">delivered</option>
              </select>
            </div>}
            <div className={css.field}><label className={css.label}>genre</label><input className={css.input} value={form.genre} onChange={e=>setForm(p=>({...p,genre:e.target.value}))} placeholder="e.g. Hip-Hop" /></div>
          </div>
          {formMode !== 'wishlist' && <>
            <div className={css.grid2}>
              <div className={css.field}><label className={css.label}>order date</label><input className={css.input} type="date" value={form.ordered} onChange={e=>setForm(p=>({...p,ordered:e.target.value}))} /></div>
              <div className={css.field}><label className={css.label}>expected delivery</label><input className={css.input} type="date" value={form.eta} onChange={e=>setForm(p=>({...p,eta:e.target.value}))} /></div>
            </div>
            <div className={css.field}><label className={css.label}>tracking number</label><input className={css.input} value={form.tracking} onChange={e=>setForm(p=>({...p,tracking:e.target.value}))} placeholder="optional" /></div>
          </>}
          <div className={css.btnRow}>
            <button className={[css.btnPrimary, css.f1].join(' ')} onClick={handleAdd}>{formMode === 'wishlist' ? 'add to wishlist' : 'add order'}</button>
            <button className={css.btnSecondary} onClick={() => { setShowAdd(false); setFormMode('ordered'); }}>cancel</button>
          </div>
          {discogs.token && <div className={css.autoEnrichNote}><Sparkles size={11} strokeWidth={1.5} style={{display:'inline',verticalAlign:'middle',marginRight:3}}/> discogs metadata fetched automatically after saving</div>}
        </div>
      )}

      {/* ── Filter + search + view controls ── */}
      {activeTab === 'collection' && (
        <div className={css.filterBar}>
          <div className={css.filterBarTop}>
            <button className={[css.filterToggle, activeFilterCount > 0 ? css.filterToggleActive : ''].filter(Boolean).join(' ')}
              onClick={() => setShowFilters(p => !p)}>
              filter{activeFilterCount > 0 ? ` ●`.repeat(activeFilterCount) : ''}
            </button>
            <input className={css.searchInput} style={{flex:1,minWidth:0}} value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="search artist, album, label…" />
            <div className={css.viewToggle}>
              {[['grid', <Grid3X3 size={14} strokeWidth={1.5}/>],['list', <List size={14} strokeWidth={1.5}/>]].map(([mode, icon]) => (
                <button key={mode} className={[css.viewBtn, viewMode === mode ? css.viewBtnActive : ''].filter(Boolean).join(' ')}
                  onClick={() => setViewMode(mode)}>{icon}</button>
              ))}
            </div>
          </div>
          {showFilters && (
            <div className={css.filterPanel}>
              <select className={css.filterSelect} value={filterYear} onChange={e => setFilterYear(e.target.value)}>
                <option value="">year</option>{years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select className={css.filterSelect} value={filterGenre} onChange={e => setFilterGenre(e.target.value)}>
                <option value="">genre</option>{genres.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
              <select className={css.filterSelect} value={filterLabel} onChange={e => setFilterLabel(e.target.value)}>
                <option value="">label</option>{labels.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <div className={css.sortGroup}>
                <span className={css.sortLabel}>sort</span>
                {[['artist','artist'],['year','year'],['added','added']].map(([val,lbl]) => (
                  <button key={val} className={[css.sortBtn, sortBy === val ? css.sortBtnActive : ''].filter(Boolean).join(' ')}
                    onClick={() => setSortBy(val)}>{lbl}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Collection section (includes ordered/incoming) ── */}
      {activeTab === 'collection' && (
        <div className={css.section}>
          <div className={css.countRow} style={{fontFamily:'var(--sans)'}}>showing {Math.min(visibleCount, filteredCollection.length)} of {filteredCollection.length}</div>
          {filteredCollection.length === 0 ? (
            <div className={css.empty}>
              <div className={css.emptyIcon}>◉</div>
              <div className={css.emptyTitle}>{collectionAndOrders.length === 0 ? 'collection empty' : 'no results'}</div>
              <div className={css.emptySub}>{collectionAndOrders.length === 0 ? 'Import from Discogs or add orders.' : 'Clear filters to see all records.'}</div>
              {collectionAndOrders.length === 0 && <button className={css.btnPrimary} onClick={() => setShowDiscogs(true)}>◉ connect discogs</button>}
            </div>
          ) : viewMode === 'grid' ? (
            <>
              <div className={css.grid}>
                {filteredCollection.slice(0, visibleCount).map(v => {
                  const isIncoming = v.status === 'ordered';
                  return (
                    <div key={v.id} className={css.gridCard} onClick={() => setSelectedRecord(v)}>
                      <div className={css.gridCover}>
                        {isIncoming
                          ? <IncomingCoverGrid record={v} />
                          : <CoverArt record={v} size="grid" />
                        }
                        {v.enriched && <div className={css.enrichedDot} />}
                      </div>
                      <div className={[css.gridArtist, isIncoming ? css.incomingText : ''].filter(Boolean).join(' ')}>{v.artist}</div>
                      <div className={[css.gridAlbum, isIncoming ? css.incomingText : ''].filter(Boolean).join(' ')}>{v.album}</div>
                      {(v.year || v.label) && <div className={css.gridMeta}>{[v.year,v.label].filter(Boolean).join(' · ')}</div>}
                    </div>
                  );
                })}
              </div>
              {filteredCollection.length > visibleCount ? (
                <div className={css.loadMoreRow}>
                  <button className={css.loadMoreBtn} onClick={() => setVisibleCount(c => c + 24)}>
                    + load 24 more ({filteredCollection.length - visibleCount} remaining)
                  </button>
                </div>
              ) : filteredCollection.length > 24 ? (
                <div className={css.allShown}>all {filteredCollection.length} records shown</div>
              ) : null}
            </>
          ) : (
            <>
              <div className={css.listWrap}>
                {filteredCollection.slice(0, visibleCount).map(v => {
                  const isIncoming = v.status === 'ordered';
                  return (
                    <div key={v.id} className={css.listRow} onClick={() => setSelectedRecord(v)}>
                      <div className={isIncoming ? css.incomingCover : undefined}>
                        <CoverArt record={v} size="list" />
                      </div>
                      <div className={css.listInfo}>
                        <div className={[css.listTitle, isIncoming ? css.incomingText : ''].filter(Boolean).join(' ')}>{v.artist} — {v.album}</div>
                        <div className={css.listMeta}>{[v.year,v.label,v.genre].filter(Boolean).join(' · ')}</div>
                      </div>
                      <div className={css.listRight} />
                    </div>
                  );
                })}
              </div>
              {filteredCollection.length > visibleCount ? (
                <div className={css.loadMoreRow}>
                  <button className={css.loadMoreBtn} onClick={() => setVisibleCount(c => c + 24)}>
                    + load 24 more ({filteredCollection.length - visibleCount} remaining)
                  </button>
                </div>
              ) : filteredCollection.length > 24 ? (
                <div className={css.allShown}>all {filteredCollection.length} records shown</div>
              ) : null}
            </>
          )}
        </div>
      )}

      {/* ── Wishlist section ── */}
      {activeTab === 'wishlist' && (
        <div className={css.section}>
          {wishlist.length === 0 ? (
            <div className={css.empty}>
              <div className={css.emptyIcon}>◉</div>
              <div className={css.emptyTitle}>wishlist empty</div>
              <div className={css.emptySub}>Add records you want to buy, or import your Discogs wishlist.</div>
              <button className={css.btnPrimary} onClick={() => { setFormMode('wishlist'); setShowAdd(true); }}>+ add to wishlist</button>
            </div>
          ) : wishlist.map(v => (
            <div key={v.id} className={css.orderCard} style={{ borderLeftColor:'var(--accent-g, var(--accent))' }}>
              <div className={css.orderTop}>
                <CoverArt record={v} size="list" />
                <div className={css.orderInfo}>
                  <div className={css.orderArtist}>{v.artist}</div>
                  <div className={css.orderAlbum}>{v.album}</div>
                </div>
                <StatusBadge s="wishlist" />
              </div>
              <div className={css.orderMeta}>
                {v.year && <span>{v.year}</span>}
                {v.label && <span>{v.label}</span>}
                {v.genre && <span>{v.genre}</span>}
              </div>
              <div className={css.orderActions}>
                <button className={[css.btnXs, css.btnXsAccent].join(' ')} onClick={() => { markOrdered?.(v.id); }}>↗ mark as ordered</button>
                {discogs.token && !v.enriched && <button className={css.btnXs} onClick={() => searchAndEnrich(v.id)} disabled={enriching===v.id}>{enriching===v.id?'…':<><Sparkles size={11} strokeWidth={1.5} style={{display:'inline',verticalAlign:'middle',marginRight:3}}/> enrich</>}</button>}
                <button className={css.btnXs} onClick={() => setSelectedRecord(v)}>details</button>
                <button className={[css.btnXs, css.btnXsDanger].join(' ')} onClick={() => deleteVinyl(v.id)}>remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Stats view ── */}
      {activeTab === 'stats' && (() => {
        const collectionItems = vinyl.filter(v => v.status === 'collection' || v.status === 'shipped');
        const totalValue = collectionItems.reduce((s,v) => s + (parseFloat(v.currentPrice || v.marketValue || 0)), 0);
        // Top artists
        const artistCounts = {};
        collectionItems.forEach(v => { if(v.artist) artistCounts[v.artist] = (artistCounts[v.artist]||0)+1; });
        const topArtists = Object.entries(artistCounts).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([name,count])=>({name,count}));
        // Recent additions
        const recentVinyl = [...collectionItems].sort((a,b)=>(b.addedAt||b.id||'').localeCompare(a.addedAt||a.id||'')).slice(0,5);
        return (
          <div className={css.section}>
            {/* value summary */}
            {totalValue > 0 && (
              <div className={css.card} style={{marginBottom:12}}>
                <div className={css.sectionLabel} style={{marginBottom:6}}>collection value</div>
                <div style={{fontSize:28,fontWeight:300,color:'var(--ink)',lineHeight:1}}>${totalValue >= 1000 ? (totalValue/1000).toFixed(1)+'k' : totalValue.toFixed(0)}</div>
                <div style={{fontSize:11,color:'var(--t3)',marginTop:4}}>estimated market value · discogs prices</div>
              </div>
            )}
            {/* top artists */}
            {topArtists.length > 0 && (
              <div className={css.card} style={{marginBottom:12}}>
                <div className={css.sectionLabel} style={{marginBottom:6}}>top artists</div>
                {topArtists.map((a,i) => (
                  <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'7px 0',borderBottom:i<topArtists.length-1?'0.5px solid var(--s2)':'none'}}>
                    <span style={{fontSize:13,color:'var(--ink)',fontFamily:'var(--sans)'}}>{a.name}</span>
                    <span style={{fontSize:12,color:'var(--t3)',fontFamily:'var(--sans)'}}>{a.count} record{a.count!==1?'s':''}</span>
                  </div>
                ))}
              </div>
            )}
            {/* recent additions */}
            {recentVinyl.length > 0 && (
              <div className={css.card} style={{marginBottom:12}}>
                <div className={css.sectionLabel} style={{marginBottom:6}}>recently added</div>
                {recentVinyl.map((v,i) => (
                  <div key={v.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'7px 0',borderBottom:i<recentVinyl.length-1?'0.5px solid var(--s2)':'none'}}>
                    <span style={{fontSize:13,color:'var(--ink)',fontFamily:'var(--sans)',flex:1,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{v.artist} — {v.album}</span>
                    <span style={{fontSize:11,color:'var(--t3)',fontFamily:'var(--sans)',flexShrink:0,marginLeft:8}}>{v.year||''}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Top labels */}
            {(() => {
              const labelCounts = {};
              collectionItems.forEach(v => { if(v.label) labelCounts[v.label] = (labelCounts[v.label]||0)+1; });
              const topLabels = Object.entries(labelCounts).sort((a,b)=>b[1]-a[1]).slice(0,3).map(([name,count])=>({name,count}));
              if (topLabels.length === 0) return null;
              return (
                <div className={css.card} style={{marginBottom:12}}>
                  <div className={css.sectionLabel} style={{marginBottom:6}}>top labels</div>
                  {topLabels.map((l,i) => (
                    <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'7px 0',borderBottom:i<topLabels.length-1?'0.5px solid var(--s2)':'none'}}>
                      <span style={{fontSize:13,color:'var(--ink)',fontFamily:'var(--sans)'}}>{l.name}</span>
                      <span style={{fontSize:12,color:'var(--t3)',fontFamily:'var(--sans)'}}>{l.count} record{l.count!==1?'s':''}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
            {/* Decade breakdown */}
            {(() => {
              const decades = {};
              collectionItems.forEach(v => {
                if (!v.year) return;
                const d = Math.floor(parseInt(v.year) / 10) * 10;
                if (!isNaN(d)) decades[d] = (decades[d]||0)+1;
              });
              const sortedDecades = Object.entries(decades).sort((a,b)=>b[0]-a[0]).map(([yr,count])=>({yr,count}));
              const maxCount = Math.max(...sortedDecades.map(d=>d.count),1);
              if (sortedDecades.length === 0) return null;
              return (
                <div className={css.card} style={{marginBottom:12}}>
                  <div className={css.sectionLabel} style={{marginBottom:6}}>by decade</div>
                  {sortedDecades.map((d,i) => (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 0',borderBottom:i<sortedDecades.length-1?'0.5px solid var(--s2)':'none'}}>
                      <div style={{fontFamily:'var(--sans)',fontSize:12,color:'var(--t3)',width:36,flexShrink:0}}>{d.yr}s</div>
                      <div style={{flex:1,height:4,background:'var(--s2)',borderRadius:2,overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${(d.count/maxCount)*100}%`,background:'var(--accent)',borderRadius:2}} />
                      </div>
                      <div style={{fontFamily:'var(--sans)',fontSize:11,color:'var(--t3)',width:20,textAlign:'right',flexShrink:0}}>{d.count}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
            {collectionItems.length === 0 && (
              <div className={css.empty}><div className={css.emptyIcon}>◉</div><div className={css.emptyTitle}>no collection data</div></div>
            )}
          </div>
        );
      })()}

      {/* ── Record detail modal ── */}
      {selectedRecord && (
        <div className={css.modalOverlay} onClick={() => setSelectedRecord(null)}>
          <div className={css.modal} onClick={e => e.stopPropagation()}>
            <div className={css.modalHandle} />
            <div className={css.modalHeader}>
              {(selectedRecord.coverArt || selectedRecord.thumb) && (
                <img src={selectedRecord.coverArt || selectedRecord.thumb} alt="" className={css.modalCover} />
              )}
              <div className={css.modalInfo}>
                <div className={css.modalLabel}>◉ vinyl record</div>
                <div className={css.modalArtist}>{selectedRecord.artist}</div>
                <div className={css.modalAlbum}>{selectedRecord.album}</div>
              </div>
            </div>
            <div className={css.modalBody}>
              {[
                { lbl:'year',      val:selectedRecord.year },
                { lbl:'label',     val:selectedRecord.label },
                { lbl:'format',    val:selectedRecord.format },
                { lbl:'genre',     val:selectedRecord.genre },
                { lbl:'cat #',     val:selectedRecord.catNum },
                { lbl:'country',   val:selectedRecord.country },
                { lbl:'status',    val:selectedRecord.status },
                { lbl:'vendor',    val:selectedRecord.vendor },
                { lbl:'ordered',   val:selectedRecord.ordered },
                { lbl:'delivered', val:selectedRecord.deliveryDate },
                { lbl:'tracking',  val:selectedRecord.tracking },
              ].filter(r => r.val).map(({ lbl, val }) => (
                <div key={lbl} className={css.modalRow}>
                  <span className={css.modalRowLabel}>{lbl}</span>
                  <span className={css.modalRowVal}>{String(val)}</span>
                </div>
              ))}
              {selectedRecord.tracklist?.length > 0 && (
                <div className={css.modalRow} style={{ flexDirection:'column', gap:'var(--sp2)' }}>
                  <span className={css.modalRowLabel}>tracks</span>
                  <span className={css.modalRowVal} style={{ fontSize:'var(--fs-sm)', color:'var(--t2)' }}>{selectedRecord.tracklist.join(' · ')}</span>
                </div>
              )}
            </div>
            <div className={css.modalActions}>
              {discogs.token && !selectedRecord.enriched && (
                <button className={[css.btnPrimary, css.f1].join(' ')} onClick={() => searchAndEnrich(selectedRecord.id)} disabled={enriching===selectedRecord.id}>
                  {enriching===selectedRecord.id ? 'enriching…' : <><Sparkles size={12} strokeWidth={1.5} style={{display:'inline',verticalAlign:'middle',marginRight:4}}/>enrich metadata</>}
                </button>
              )}
              {/* Status transitions */}
              {selectedRecord.status === 'wishlist' && (
                <button className={css.btnMarkPlaying} style={{ borderColor:'var(--accent-g,var(--accent))', color:'var(--accent-g,var(--accent))' }} onClick={() => { markOrdered?.(selectedRecord.id); setSelectedRecord(null); }}>↗ mark as ordered</button>
              )}
              {selectedRecord.status === 'ordered' && (
                <>
                  <button className={[css.btnXsAccent, css.btnSecondary].join(' ')} style={{ fontSize:'var(--fs-sm)' }} onClick={() => { markDelivered?.(selectedRecord.id); setSelectedRecord(null); }}>✓ mark delivered</button>
                  <button className={css.btnSecondary} style={{ fontSize:'var(--fs-sm)', color:'var(--t3)' }} onClick={() => { markWishlist?.(selectedRecord.id); setSelectedRecord(null); }}>← wishlist</button>
                </>
              )}
              {selectedRecord.status === 'collection' && (
                <button className={css.btnSecondary} style={{ fontSize:'var(--fs-sm)', color:'var(--t3)' }} onClick={() => { markWishlist?.(selectedRecord.id); setSelectedRecord(null); }}>← wishlist</button>
              )}
              {/* Now playing toggle */}
              {nowPlayingId === selectedRecord.id ? (
                <button className={css.btnStopPlaying} onClick={() => clearNowPlaying?.()}>■ stop playing</button>
              ) : (
                <button className={css.btnMarkPlaying} onClick={() => { setNowPlaying?.(selectedRecord.id); setSelectedRecord(null); }}>▶ now playing</button>
              )}
              {selectedRecord.discogsUrl && (
                <a href={selectedRecord.discogsUrl} target="_blank" rel="noopener noreferrer" className={[css.btnSecondary, css.f1].join(' ')} style={{ textDecoration:'none', display:'flex', alignItems:'center', justifyContent:'center' }}>discogs ↗</a>
              )}
              <button className={css.btnSecondary} onClick={() => setSelectedRecord(null)}>close</button>
              <button className={css.btnDanger} onClick={() => { deleteVinyl(selectedRecord.id); setSelectedRecord(null); }}>remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
